import { FC, useState } from 'react';

import { IService } from 'interfaces/services';
import Calendar from './Calendar/Calendar';
import moment from 'moment';
import useScheduleNavigator from 'hooks/useScheduleNavigator';
import MonthViewNavigation from 'components/MonthViewNavigation/MonthViewNavigation';
import { NEXT_23_MONTH, SERVICE_NAME } from 'utils/constants';
import CommonButton from 'components/CommonButton/CommonButton';
import AdvancedSetting from './AdvancedSetting/AdvancedSetting';
import useClinicSchedule from 'hooks/useClinicSchedule';
import useClinicException from 'hooks/useClinicException';

import ThreeDotsOption from 'pages/ServicesPage/Table/ThreeDotsOption/ThreeDotsOption';
import HygieneChairSection from 'pages/ServicesPage/HygieneChairSection/HygieneChairSection';
import { useServiceSidebar } from '../context/serviceSidebarContext';
import { KeyedMutator } from 'swr';
import { IUseClinicServiceResponse } from 'hooks/useClinicService';
import { renderToast } from 'components/Toast/Toast';

interface MainSectionProps {
  selectedService: IService;
  setSelectedService: React.Dispatch<React.SetStateAction<IService | null>>;
  clinic: any;
  isHygieneChairSetup: boolean;
  services: any;
  mutate: KeyedMutator<IUseClinicServiceResponse>;
}

const MainSection: FC<MainSectionProps> = ({
  selectedService,
  clinic,
  isHygieneChairSetup,
  services,
  setSelectedService,
  mutate,
}) => {
  const today = moment().tz(clinic.timezone).format('YYYY-MM-DD');
  const { state, dispatch } = useScheduleNavigator(today);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { setIsServiceCollapsed } = useServiceSidebar();

  const { data: clinicSchedule } = useClinicSchedule();

  const { data: clinicException } = useClinicException({
    source: clinicSchedule ? clinicSchedule.currentWorkingHour : '',
    shouldRun: !!clinicSchedule,
    startDate: state.monthValue,
    endDate: moment(state.monthValue).endOf('month').format('YYYY-MM-DD'),
  });

  const onSuccessfullyEdited = async () => {
    try {
      const result = await mutate();
      if (!result) return;
      const response = result;
      const updatedServices = response?.data ?? [];
      let service = updatedServices.find(
        (item: IService) => selectedService.id === item.id
      );
      const parentExamCleaning = updatedServices.find(
        (item: IService) => item.name === SERVICE_NAME.EXAM_CLEANING
      );
      if (parentExamCleaning && !service) {
        service = parentExamCleaning.childServices.find(
          (item: IService) => item.id === selectedService.id
        );
      }
      if (!service) {
        setSelectedService(updatedServices[0]);
        return;
      }
      setSelectedService(service!);
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Please refresh the page ',
      });
    }
  };

  const onDrawerOpen = () => {
    setIsDrawerOpen(true);
    setIsServiceCollapsed(true);
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
    setIsServiceCollapsed(false);
  };

  return (
    <div
      className="relative flex-1 px-4 pt-2.4 text-14 leading-[2.1rem]"
      key={`${selectedService.id}`}
    >
      <div className="flex items-center">
        <h3 className="text-24 leading-[3.6rem] font-bold">
          {selectedService.displayName
            ? selectedService.displayName
            : selectedService.name}
        </h3>

        <div className="ml-[10px]">
          <ThreeDotsOption
            isVisibilitySectionEnabled
            service={selectedService}
            isSubRow={
              selectedService.name.includes(SERVICE_NAME.EXAM_CLEANING) &&
              selectedService.name !== SERVICE_NAME.EXAM_CLEANING
            }
            isParent={selectedService.name === SERVICE_NAME.EXAM_CLEANING}
            isHygieneChairSetup={isHygieneChairSetup}
            timeInterval={clinic.timeInterval}
            clinicServices={services}
            isDoctorSelectionEnabled={clinic.isDoctorSelectionEnabled}
            isHidden={false}
            onSuccessfullyEdited={onSuccessfullyEdited}
            dropDownPositionClassName="left-0"
            isClinicBlockSettingEnabled
          />
        </div>
      </div>

      {selectedService.name === SERVICE_NAME.EXAM_CLEANING && (
        <HygieneChairSection />
      )}
      {selectedService.name !== SERVICE_NAME.EXAM_CLEANING &&
        selectedService.isBlockSchedulingEnabled && (
          <>
            <div className="flex-1">
              <div className="flex flex-row justify-between items-center relative">
                <MonthViewNavigation
                  date={state.monthValue}
                  setDate={(date) =>
                    dispatch({
                      type: 'monthNavigation',
                      payload: { monthValue: date },
                    })
                  }
                  maxDate={moment()
                    .add(NEXT_23_MONTH, 'months')
                    .endOf('days')
                    .toDate()}
                />

                <CommonButton
                  variant="secondary"
                  className=" !min-w-[18rem]"
                  onClick={onDrawerOpen}
                >
                  Advanced Settings
                </CommonButton>
              </div>
            </div>
            <div className="flex gap-x-1">
              <div className="flex-1">
                <Calendar
                  service={selectedService}
                  state={state}
                  dispatch={dispatch}
                  clinicException={clinicException}
                />
              </div>
              {isDrawerOpen && <div className="w-40" />}
            </div>
            <AdvancedSetting
              key={`${isDrawerOpen}`}
              service={selectedService}
              isDrawerOpen={isDrawerOpen}
              onClose={onDrawerClose}
              services={services}
              onSuccessfullyEdited={onSuccessfullyEdited}
            />
          </>
        )}
      {selectedService.name !== SERVICE_NAME.EXAM_CLEANING &&
        !selectedService.isBlockSchedulingEnabled && (
          <div className="flex items-center justify-center h-[400px]">
            <p className="text-16 leading-[2.4rem] text-center text-darkest-grey">
              Block scheduling is turned off for this service. Please turn on
              block scheduling to view this setting.
            </p>
          </div>
        )}
    </div>
  );
};

export default MainSection;
